// Color editor popover (Task 2).
// Two tabs:
//   Color  - hex / RGB / Lab inputs, native color picker, eyedropper
//            (EyeDropper API when the browser provides it), gamut warnings.
//   Library - user-imported swatch library (ASE / CSV / JSON), search by
//            name/code, nearest match with CIEDE2000.
// "Apply" writes to the ACTIVE colorway only (one-directional recoloring;
// alpha maps are never touched here).

import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { LabColor, PaletteColor, Swatch } from '@imagelayerdecomposer/core';
import {
  labToHex,
  hexToLab,
  labToSrgb,
  srgbToLab,
  gamutWarning,
  isInSrgbGamut,
  parseSwatchFile,
  parseSwatchJson,
  findNearestSwatch,
  searchSwatches,
  deltaE2000,
} from '@imagelayerdecomposer/core';

export interface SwatchLibrary {
  name: string;
  swatches: Swatch[];
}

interface ColorEditorPopoverProps {
  color: PaletteColor;
  label: string;
  anchor: { top: number; left: number }; // viewport coords for fixed pos
  library: SwatchLibrary | null;
  onLibraryChange: (lib: SwatchLibrary | null) => void;
  onApply: (lab: LabColor) => void;
  onClose: () => void;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

export default function ColorEditorPopover({
  color,
  label,
  anchor,
  library,
  onLibraryChange,
  onApply,
  onClose,
}: ColorEditorPopoverProps) {
  const [tab, setTab] = useState<'color' | 'library'>('color');
  const [lab, setLab] = useState<LabColor>({ ...color.lab });
  const [hexDraft, setHexDraft] = useState(() => labToHex(color.lab));
  const [hexError, setHexError] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [loadingLib, setLoadingLib] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Browser eyedropper (Chromium); not available in Firefox/Safari.
  const EyeDropperCtor = (window as any).EyeDropper as
    | (new () => { open(): Promise<{ sRGBHex: string }> })
    | undefined;

  // Re-sync when a different row is opened
  useEffect(() => {
    setLab({ ...color.lab });
    setHexDraft(labToHex(color.lab));
    setHexError(false);
    setSelectedIdx(null);
    setImportError(null);
  }, [color.id, color.lab.L, color.lab.a, color.lab.b]);

  const srgb = labToSrgb(lab);
  const rgb255 = {
    r: Math.round(srgb.r * 255),
    g: Math.round(srgb.g * 255),
    b: Math.round(srgb.b * 255),
  };
  const warning = gamutWarning(lab);
  const inGamut = isInSrgbGamut(lab);

  const applyLab = (next: LabColor) => {
    setLab(next);
    setHexDraft(labToHex(next));
    setHexError(false);
  };

  const applyHex = (raw: string) => {
    setHexDraft(raw);
    const parsed = hexToLab(raw);
    if (parsed) {
      setLab(parsed);
      setHexError(false);
    } else {
      setHexError(true);
    }
  };

  const applyRgb = (r: number, g: number, b: number) => {
    applyLab(
      srgbToLab({
        r: clamp(r, 0, 255) / 255,
        g: clamp(g, 0, 255) / 255,
        b: clamp(b, 0, 255) / 255,
      })
    );
  };

  const runEyedropper = async () => {
    if (!EyeDropperCtor) return;
    try {
      const ed = new EyeDropperCtor();
      const res = await ed.open();
      if (res?.sRGBHex) applyHex(res.sRGBHex);
    } catch {
      // user cancelled the picker — ignore
    }
  };

  /**
   * Load a library that already lives in the app's public folder.
   * `scripts/pantone_to_swatch.py` generates it from the local Pantone tables
   * (offline; no data is bundled in the repo by default).
   */
  const loadLocalLibrary = async () => {
    setImportError(null);
    setLoadingLib(true);
    try {
      const res = await fetch('/libraries/pantone.json');
      if (!res.ok) {
        throw new Error(
          `libraries/pantone.json not found (HTTP ${res.status}). ` +
            'Run: python scripts/merge_pantone_library.py'
        );
      }
      const swatches = parseSwatchJson(await res.text());
      onLibraryChange({ name: 'Pantone (local)', swatches });
      setSelectedIdx(null);
    } catch (err: any) {
      setImportError(err?.message ?? String(err));
    } finally {
      setLoadingLib(false);
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportError(null);
    try {
      const buf = await file.arrayBuffer();
      const swatches = parseSwatchFile(file.name, buf);
      onLibraryChange({ name: file.name, swatches });
      setSelectedIdx(null);
    } catch (err: any) {
      setImportError(err?.message ?? String(err));
    } finally {
      e.target.value = '';
    }
  };

  const filtered = useMemo(
    () => (library ? searchSwatches(library.swatches, query) : []),
    [library, query]
  );

  const nearest = useMemo(() => {
    if (!library) return null;
    return findNearestSwatch(library.swatches, lab);
  }, [library, lab]);

  const shown = filtered.slice(0, 300);

  // Position: to the left of the dock panel so it never leaves the viewport
  const popStyle: React.CSSProperties = {
    position: 'fixed',
    top: Math.max(8, Math.min(anchor.top, window.innerHeight - 470)),
    left: Math.max(8, anchor.left - 316),
  };

  const numInput = (
    value: number,
    onChange: (v: number) => void,
    step = 1
  ) => (
    <input
      type="number"
      step={step}
      value={Number.isFinite(value) ? Math.round(value * 10) / 10 : 0}
      onChange={(e) => {
        const v = parseFloat(e.target.value);
        if (Number.isFinite(v)) onChange(v); // ignore half-typed / cleared input
      }}
    />
  );

  return (
    <>
      <div className="popover-backdrop" onClick={onClose} />
      <div className="color-popover" style={popStyle} role="dialog" aria-label="Color editor">
        <div className="popover-header">
          <span
            className="popover-swatch"
            style={{ background: labToHex(lab) }}
          />
          <span className="popover-title">{label}</span>
          <button className="icon-btn" onClick={onClose} title="Close">
            ×
          </button>
        </div>

        <div className="popover-tabs">
          <button
            className={tab === 'color' ? 'active' : ''}
            onClick={() => setTab('color')}
          >
            Color
          </button>
          <button
            className={tab === 'library' ? 'active' : ''}
            onClick={() => setTab('library')}
          >
            Library
          </button>
        </div>

        {tab === 'color' && (
          <div className="popover-body">
            <div className="field-row">
              <label>Hex</label>
              <input
                type="text"
                className={hexError ? 'invalid' : ''}
                value={hexDraft}
                onChange={(e) => applyHex(e.target.value)}
                spellCheck={false}
              />
              <input
                type="color"
                value={labToHex(lab)}
                onChange={(e) => applyHex(e.target.value)}
                title="Color picker"
              />
              <button
                className="btn small"
                onClick={runEyedropper}
                disabled={!EyeDropperCtor}
                title={
                  EyeDropperCtor
                    ? 'Pick a color from the screen'
                    : 'Eyedropper not supported in this browser'
                }
              >
                Pipette
              </button>
            </div>
            {hexError && (
              <div className="warn-line">Invalid hex — use #rrggbb.</div>
            )}

            <div className="field-row three">
              <label>R</label>
              {numInput(rgb255.r, (v) => applyRgb(v, rgb255.g, rgb255.b))}
              <label>G</label>
              {numInput(rgb255.g, (v) => applyRgb(rgb255.r, v, rgb255.b))}
              <label>B</label>
              {numInput(rgb255.b, (v) => applyRgb(rgb255.r, rgb255.g, v))}
            </div>

            <div className="field-row three">
              <label>L</label>
              {numInput(lab.L, (v) => applyLab({ ...lab, L: clamp(v, 0, 100) }))}
              <label>a</label>
              {numInput(lab.a, (v) => applyLab({ ...lab, a: clamp(v, -128, 128) }))}
              <label>b</label>
              {numInput(lab.b, (v) => applyLab({ ...lab, b: clamp(v, -128, 128) }))}
            </div>

            {warning ? (
              <div className={`warn-line ${inGamut ? '' : 'strong'}`}>⚠ {warning}</div>
            ) : (
              <div className="hint-line">In sRGB gamut</div>
            )}

            <div className="popover-footer">
              <button className="btn" onClick={onClose}>
                Cancel
              </button>
              <button
                className="btn primary"
                onClick={() => {
                  onApply(lab);
                  onClose();
                }}
              >
                Apply
              </button>
            </div>
          </div>
        )}

        {tab === 'library' && (
          <div className="popover-body">
            <div className="field-row">
              <button className="btn small" onClick={() => fileRef.current?.click()}>
                Import ASE / CSV / JSON…
              </button>
              <input
                ref={fileRef}
                type="file"
                accept=".ase,.csv,.json,.txt"
                style={{ display: 'none' }}
                onChange={handleImport}
              />
            </div>
            <div className="field-row">
              <button
                className="btn small"
                onClick={loadLocalLibrary}
                disabled={loadingLib}
                title="Load apps/web/public/libraries/pantone.json (generated from your local Pantone tables)"
              >
                {loadingLib ? 'Loading…' : 'Load Pantone (local)'}
              </button>
            </div>
            <div className="hint-line">
              No color data is bundled — import your own swatch files.
            </div>
            {importError && <div className="warn-line strong">⚠ {importError}</div>}

            {library && (
              <>
                <div className="field-row">
                  <input
                    type="text"
                    placeholder="Search name / code…"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setSelectedIdx(null);
                    }}
                  />
                </div>
                <div className="lib-meta">
                  {library.name}: {filtered.length} / {library.swatches.length} shown
                </div>
                <div className="lib-list">
                  {shown.map((s) => {
                    const idx = library.swatches.indexOf(s);
                    const de = deltaE2000(s.lab, lab);
                    const isNearest = nearest && nearest.swatch === s;
                    return (
                      <div
                        key={`${s.name}-${idx}`}
                        className={`lib-row${selectedIdx === idx ? ' selected' : ''}${
                          isNearest ? ' nearest' : ''
                        }`}
                        onClick={() => setSelectedIdx(idx)}
                        onDoubleClick={() => {
                          onApply(s.lab);
                          onClose();
                        }}
                        title={isNearest ? 'Nearest match to current color' : undefined}
                      >
                        <span className="lib-swatch" style={{ background: labToHex(s.lab) }} />
                        <span className="lib-name">{s.name}</span>
                        {s.code && <span className="lib-code">{s.code}</span>}
                        <span className="lib-de">ΔE00 {de.toFixed(1)}</span>
                      </div>
                    );
                  })}
                  {shown.length === 0 && (
                    <div className="hint-line">No matches.</div>
                  )}
                </div>
              </>
            )}

            <div className="popover-footer">
              <span className="hint-line">
                {nearest
                  ? `Nearest: ${nearest.swatch.name} (ΔE00 ${nearest.deltaE.toFixed(2)})`
                  : 'No library loaded'}
              </span>
              <button
                className="btn primary"
                disabled={selectedIdx === null || !library}
                onClick={() => {
                  if (library && selectedIdx !== null) {
                    onApply(library.swatches[selectedIdx].lab);
                    onClose();
                  }
                }}
              >
                Use selected
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
